/**
 * LES ÉCRANS DU BUREAU — la liste FIXE des applications du cockpit admin.
 *
 * Kayla, 08-10-2026 : « je veux complètement supprimer le délire de l'accueil,
 * comme dans Windows, de la barre de tâches. Je veux le simplifier au maximum,
 * le rendre le maximum minimaliste possible. » Le Bureau à icônes,
 * l'Explorateur, le DozStore, les épingles et leur menu contextuel sont
 * partis le même jour. Ce qui reste : UNE colonne d'icônes dans le rail
 * gauche, la même pour tout le monde, filtrée par les droits — exactement le
 * modèle du kiosque des chatteurs (`EcransDuKiosque`), sans le verrou.
 *
 * Son tri, le même jour, mot pour mot : « Spaces, ça devrait être dans
 * Telegram. Sécurité : Telegram aussi. Créateur : OK. Twitter : OK. […]
 * Twitter site : dans Twitter. Validation Vault : [avec le Vault].
 * Site : dans Telegram. DOZIX [DozStore] : supprimé. Formation : dans
 * l'organisation. Sans réponse et relance : dans l'organisation. » Ces
 * écrans-là ne sont donc plus des applications : ils sont des ONGLETS de
 * leur hôte (`TelegramPanel`, `CreatorsPanel`, `OrganisationWorkspace`), et
 * `hidden: true` dans le registre les retire du picker « Ajouter un panneau »
 * — une chose, un seul endroit.
 *
 * ⚠️ Ce n'est pas un contrôle d'accès : `isPanelVisible` (permissions,
 * capabilities, Lab, Métriques) filtre la liste au rendu, et `PanelHost`
 * refuse ce qu'un rôle n'a pas le droit d'ouvrir.
 *
 * L'ordre est celui du rail, de haut en bas. Le rail se dessine comme la
 * colonne « Piloter » de Helios : une icône et son nom par ligne, jamais une
 * barre de tâches (Kayla, 08-10-2026 : « je t'ai dit d'enlever la barre des
 * tâches […] tu peux faire comme dans Helios »).
 */
import { useStore } from "jotai";
import { useCallback } from "react";

import { isMobileSync } from "@/core/hooks/useIsMobile";
import {
  focusedGroupIdAtom,
  layoutAtom,
  mobileActiveInstanceIdAtom,
} from "@/core/state";
import {
  addPanel,
  findGroupContaining,
  findNodeById,
  listAllPanelInstances,
  setActiveTab,
  type AddTarget,
} from "@/shell/layout";
import type { PanelId } from "@/shell/PanelRegistry";

export const ECRANS_DU_BUREAU: readonly PanelId[] = [
  "conversations",
  // L'Analytique (Kayla, 09-10-2026) : l'argent, les associés, l'acquisition,
  // les pays — « une nouvelle app, très visuelle ».
  "analytique",
  "telegram",
  "twitter",
  "creators",
  "organisation",
  "cyrano",
  "lab",
  "herald",
  "personnel",
  // 🪦 La Régie, le catalogue d'offres et la Documentation en sont sortis une
  // heure après le premier rail (Kayla, 08-10-2026 : « la régie, faut
  // supprimer » ; « Catalogue d'offres, on s'en fout » ; « je ne sais pas
  // pourquoi j'ai une application de documentation alors que je ne suis pas un
  // chatteur »). La doc reste l'onglet des chatteurs (`KIOSK_TABS`).
];

/**
 * Ouvrir un écran, ou le remettre devant s'il est déjà ouvert.
 *
 * Une seule instance par écran : s'il en existe une dans le layout, on
 * active son onglet et on focalise son groupe. Sinon, on l'ouvre en onglet du
 * groupe FOCALISÉ (celui où l'on a cliqué en dernier), à défaut du groupe
 * actif. C'est ce que faisait `useOpenApp` pour les apps `single` — sans la
 * couche « application » au-dessus du registre, qui n'existe plus.
 *
 * `params` force une NOUVELLE instance (un écran contextuel) ; sans eux, on
 * retrouve l'existante.
 */
export function useOuvrirEcran(): (
  panelId: PanelId,
  params?: Record<string, unknown>,
) => void {
  const store = useStore();

  return useCallback(
    (panelId, params) => {
      const layout = store.get(layoutAtom);

      if (!params) {
        const existante = listAllPanelInstances(layout).find(
          (i) => i.panelId === panelId,
        );
        if (existante) {
          const groupe = findGroupContaining(layout, existante.id);
          if (groupe) {
            const index = groupe.panels.findIndex((p) => p.id === existante.id);
            if (index >= 0) {
              store.set(layoutAtom, (prev) => setActiveTab(prev, groupe.id, index));
              store.set(focusedGroupIdAtom, groupe.id);
              if (isMobileSync()) store.set(mobileActiveInstanceIdAtom, existante.id);
              return;
            }
          }
        }
      }

      const focaliseId = store.get(focusedGroupIdAtom);
      const focalise = focaliseId ? findNodeById(layout, focaliseId) : null;
      const cible: AddTarget =
        focalise && focalise.kind === "tabs"
          ? { kind: "group", groupId: focaliseId! }
          : { kind: "active-group" };

      let nouvelleId = "";
      store.set(layoutAtom, (prev) => {
        const res = addPanel(prev, panelId, cible, params ? { params } : undefined);
        nouvelleId = res.instanceId;
        return res.layout;
      });
      if (isMobileSync() && nouvelleId) {
        store.set(mobileActiveInstanceIdAtom, nouvelleId);
      }
    },
    [store],
  );
}
